import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { UserCog, Users, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { UserController } from '@/presentation/controllers/user-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useUserForm } from '@/presentation/hooks/use-user-form.js';
import { UserAssignmentsTab } from '@/presentation/pages/users/components/tabs/user-assignments-tab.js';
import { UserDadosTab } from '@/presentation/pages/users/components/tabs/user-dados-tab.js';
import { resolveAvailableProfiles } from '@/presentation/pages/users/utils/resolve-available-profiles.js';
import { validateUserForm } from '@/presentation/pages/users/utils/validate-user-form.js';
import { gradientButtonSx } from '@/shared/utils/brand-gradient.js';
import { formatDate } from '@/shared/utils/format-date.js';

type TabKey = 'dados' | 'associacoes';

interface Props {
    open: boolean;
    onClose: () => void;
    onSaved: (message: string) => void;
    controller: UserController;
    mode: 'create' | 'edit';
    userId?: string;
}

export function UserFormDialog({ open, onClose, onSaved, controller, mode, userId }: Props) {
    const { translate, useAuthStore } = useApp();
    const me = useAuthStore((s) => s.user);
    const isEdit = mode === 'edit';
    const isAdm = me?.isGlobalAdmin === true;

    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState<TabKey>('dados');
    const [formError, setFormError] = useState<string | null>(null);
    const {
        loading,
        name,
        email,
        phone,
        status,
        createdAt,
        lastLoginAt,
        assignments,
        clients,
        projectsMap,
        errors,
        setName,
        setEmail,
        setPhone,
        setErrors,
        addAssignment,
        removeAssignment,
        updateAssignment
    } = useUserForm(controller, open, isEdit, userId);

    useEffect(() => {
        if (open) {
            setActiveTab('dados');
            setFormError(null);
        }
    }, [open]);

    const availableProfiles = resolveAvailableProfiles(isAdm);
    const profileOptions = availableProfiles.map((p) => ({ value: p, label: translate(`profiles.${p}`) }));

    const handleSave = async () => {
        const errs = validateUserForm({ name, email, isEdit, assignments }, translate);
        setErrors(errs);
        if (Object.keys(errs).length > 0) {
            if (errs.assignments && activeTab === 'dados') {
                setActiveTab('associacoes');
            }
            return;
        }
        setSaving(true);
        setFormError(null);
        try {
            const validAssignments = assignments
                .filter((a) => a.clientId && a.profile)
                .map((a) => ({ clientId: a.clientId, projectId: a.projectId || undefined, profile: a.profile, isActive: true }));

            if (isEdit && userId) {
                const updateResult = await controller.updateUser(userId, { name: name.trim(), phone: phone.trim() || undefined });
                if (updateResult.isLeft()) {
                    setFormError(updateResult.value.message || translate('errors.generic'));
                    setErrors({ name: updateResult.value.message || translate('errors.generic') });
                    setActiveTab('dados');
                    return;
                }
                const assignmentsResult = await controller.saveUserAssignments(userId, validAssignments);
                if (assignmentsResult.isLeft()) {
                    setFormError(assignmentsResult.value.message || translate('errors.generic'));
                    setErrors({ assignments: assignmentsResult.value.message || translate('errors.generic') });
                    setActiveTab('associacoes');
                    return;
                }
            } else {
                const result = await controller.createUser({
                    name: name.trim(),
                    email: email.trim().toLowerCase(),
                    phone: phone.trim() || undefined,
                    assignments: validAssignments
                });
                if (result.isLeft()) {
                    setFormError(result.value.message || translate('errors.generic'));
                    setErrors({ name: result.value.message || translate('errors.generic') });
                    setActiveTab('dados');
                    return;
                }
            }
            onSaved(isEdit ? translate('users.updatedMsg') : translate('users.createdMsg'));
            onClose();
        } finally {
            setSaving(false);
        }
    };

    const tabs = [
        { key: 'dados' as TabKey, label: translate('users.tabs.dados'), icon: UserCog },
        { key: 'associacoes' as TabKey, label: translate('users.tabs.assignments'), icon: Users }
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6" fontWeight={700} color="text.primary">
                            {isEdit ? translate('users.edit') : translate('users.create')}
                        </Typography>
                        {isEdit && email && (
                            <Typography variant="caption" color="text.secondary">
                                {email}
                            </Typography>
                        )}
                    </Stack>
                    {isEdit && createdAt && (
                        <Typography variant="caption" color="text.secondary">
                            {translate('users.createdAt')}: {formatDate(createdAt)}
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={onClose} size="small">
                    <X size={20} />
                </IconButton>
            </DialogTitle>

            <Tabs value={activeTab} onChange={(_, v: TabKey) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
                {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return <Tab key={tab.key} value={tab.key} icon={<Icon size={16} />} iconPosition="start" label={tab.label} sx={{ textTransform: 'none', minHeight: 44 }} />;
                })}
            </Tabs>

            <DialogContent sx={{ p: 3 }}>
                {formError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {formError}
                    </Alert>
                )}
                {loading ? (
                    <Typography>{translate('common.loading')}</Typography>
                ) : (
                    <>
                        {activeTab === 'dados' && (
                            <UserDadosTab
                                name={name}
                                email={email}
                                phone={phone}
                                status={status}
                                isEdit={isEdit}
                                errors={errors}
                                labels={{
                                    personalData: translate('users.personalData'),
                                    nameLabel: translate('users.nameLabel'),
                                    namePlaceholder: translate('users.namePlaceholder'),
                                    emailLabel: translate('users.emailLabel'),
                                    emailPlaceholder: translate('users.emailPlaceholder'),
                                    emailReadOnly: translate('users.emailReadOnly'),
                                    phoneLabel: translate('users.phoneLabel'),
                                    phonePlaceholder: translate('users.phonePlaceholder'),
                                    status: translate('users.status'),
                                    active: translate('users.active'),
                                    blocked: translate('users.blocked')
                                }}
                                onNameChange={setName}
                                onEmailChange={setEmail}
                                onPhoneChange={setPhone}
                            />
                        )}

                        {activeTab === 'associacoes' && (
                            <UserAssignmentsTab
                                assignments={assignments}
                                clients={clients}
                                projectsMap={projectsMap}
                                profiles={profileOptions}
                                errors={errors}
                                isEdit={isEdit}
                                lastLoginAt={lastLoginAt}
                                labels={{
                                    assignments: translate('users.assignments'),
                                    addAssignment: translate('users.addAssignment'),
                                    lastLogin: translate('users.lastLogin'),
                                    client: translate('users.client'),
                                    projectOptional: translate('users.projectOptional'),
                                    profileLabel: translate('users.profileLabel'),
                                    selectClient: translate('users.selectClient'),
                                    allProjectsOption: translate('users.allProjectsOption'),
                                    removeAssignment: translate('common.remove')
                                }}
                                onAdd={addAssignment}
                                onUpdate={updateAssignment}
                                onRemove={removeAssignment}
                            />
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
                    {translate('common.cancel')}
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={saving || loading} disableElevation sx={gradientButtonSx}>
                    {saving ? translate('users.savingUser') : translate('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
